import {FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {FormData} from "@t/fd/FormPart.ts";
import {getter} from "@fd/Part.ts";
import {inject, type InjectionKey, type ComputedRef, computed} from "vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";

export type ComputedContext = ComputedRef<AppContextMap>;
export const contextKey = Symbol() as InjectionKey<ComputedContext>;
export const EMPTY_CONTEXT = computed<AppContextMap>(() => { return {} });

export function injectContext():ComputedContext {
	return inject(contextKey, EMPTY_CONTEXT);
}


export const ContextKey = {
    ID: "id",
	SRC_ID: "srcId",
	SRC_TYPE: "srcType",
	SRC_SUB_TYPE: "srcSubType",
	PARENT: "parent",

    CUSTOMER: "customer",
    BRANCH: "branch",
    PERSON: "person",
    OPPORTUNITY: "opportunity",
    CONTACT: "contact",
    EVENT: "event",
    BUSCASE: "busCase",
    CAMPAIGN: "campaign",
    PROBLEM: "problem",
    WORKSHEET: "worksheet",
    OFFER: "offer",
    PROJECT: "parentProjectId",
    TASK: "task",
    SPEC_DIARY: "specDiary",
	MAIL: "mail",			/* Mail Message */
	COMPONENT: "component",	/* Work Component */
};

/** Context params required by different row objects  **/
export const ACTIVITY_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON, ContextKey.OPPORTUNITY, ContextKey.BUSCASE, ContextKey.CAMPAIGN, ContextKey.PROJECT, ContextKey.PROBLEM];
export const OFFER_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON, ContextKey.OPPORTUNITY, ContextKey.OFFER, ContextKey.CAMPAIGN];
export const BUSCASE_REFS:string[] = OFFER_REFS;
export const PROBLEM_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON, ContextKey.CONTACT, ContextKey.OPPORTUNITY, ContextKey.BUSCASE, ContextKey.CAMPAIGN, ContextKey.COMPONENT ];
export const OPPORTUNITY_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON, ContextKey.CAMPAIGN ];
export const ACCDOC_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON, ContextKey.OPPORTUNITY, ContextKey.BUSCASE ];
export const WORKSHEET_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON,
	ContextKey.CAMPAIGN, ContextKey.OPPORTUNITY, ContextKey.OFFER, ContextKey.BUSCASE, ContextKey.PROBLEM, ContextKey.TASK, ContextKey.EVENT];
export const CONTACT_REFS:string[] = [...ACTIVITY_REFS];
export const EVENT_REFS:string[] = [...ACTIVITY_REFS];
export const TASK_REFS:string[] = [...ACTIVITY_REFS];
export const FACT_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH, ContextKey.PERSON];
// Used to pass context to e-mail address suggester
export const MAIL_SELECT_REFS:string[] = [ContextKey.CUSTOMER, ContextKey.BRANCH];


export interface AppContextMap {
    [key: string]: string|number|boolean
}

export function cloneMap(map:AppContextMap):AppContextMap {
	return { ...map };
}

export function clearMap(map:AppContextMap):void {
	Object.keys(map).forEach(key => delete map[key]);
}

export function mergeMaps(map:AppContextMap, other:AppContextMap):void {
	Object.keys(other).forEach(key => map[key] = other[key]);
}

export function addToMap(map:AppContextMap, key: string, value: string | number | boolean):void {
	map[key] = value;
}

export function addTruthyToMap(map:AppContextMap, key: string, value: string | number | boolean | undefined):void {
	if (value) map[key] = value;
}

export function hasValue(map:AppContextMap, key: string):boolean {
	const v = map[key];
	return !(v == null || v === 0 || v === "0");
}

export function hasAnyValue(map:AppContextMap, ...keys: string[]):boolean {
	for (const key of keys) {
		if (hasValue(map, key)) return true;
	}
	return false;
}

export function getValue(map:AppContextMap, key: string):string|number|boolean|undefined {
	return map[key];
}

export function getIntValue(map:AppContextMap, key: string):number|undefined {
	let v = map[key];
	switch (typeof v) {
		case "number":
			return v;
		case "boolean":
			return v ? 1 : 0;
		case "string":
			return parseInt(v);
		default:
			return undefined;
	}
}

export function filterMap(map:AppContextMap, ...allowed:string[]):AppContextMap {
	const filtered:AppContextMap = {};
	for (let key of allowed) {
		if (hasValue(map, key)) filtered[key] = map[key];
	}
	return filtered;
}

export function removeFromMap(map:AppContextMap, key:string):string | number | boolean {
	const current = map[key];
	delete map[key];
	return current;
}

export function toQueryString(map:AppContextMap, include?:string[]) {
	const params = [];
	for (let key of Object.keys(map)) {
		if (include && include.indexOf(key) < 0) continue;
		const value = map[key];
		params.push(key+"="+encodeURIComponent(value.toString()));
	}
	return params.join("&");
}

export function emptyMap(map:AppContextMap) {
	const keys = Object.keys(map);
	if (keys.length === 0) return true;
	for (let key of keys) {
		const value = map[key];
		if (value) return false;
	}
	return true;
}

export function parseQueryString(query:string):AppContextMap {
	const map:AppContextMap = {};
	if (query) {
		if (query.startsWith("?")) query = query.substring(1);
		const url = new URL("http://sample.com?" + query);
		for (let [key, value] of url.searchParams.entries()) {
			map[key] = value;
		}
	}
	return map;
}

export function contextBuilder(fd:InterfaceRoot<FormDefinition>, data:FormData, external?:AppContextMap):AppContextMap {
	const ctx:AppContextMap = {};

	// static fixed
	if (fd.context && fd.context.fixed) {
		mergeMaps(ctx, fd.context.fixed);
	}
	// external
	if (external) {
		mergeMaps(ctx, external);
	}
	// dynamic
	if (fd.context && Array.isArray(fd.context.data)) {
		for (let rule of fd.context.data) {
			let value = getter(data, rule.d, data);
			if (value == null) continue;
			switch (typeof value) {
				case "string":
					if (value.length > 0) addToMap(ctx, rule.name, value);
					break;
				case "number":
					if (value !== 0) addToMap(ctx, rule.name, value);
					break;
				case "boolean":
					addToMap(ctx, rule.name, value);
					break;
			}
		}
	}
	return ctx;
}

export function addContextParams(endpoint: string, ctx?: AppContextMap, addCtx:boolean|string[] = true):string {
	if (ctx && !emptyMap(ctx) && addCtx) {
		const separ = endpoint.indexOf("?") > 0 ? "&" : "?";
		if (addCtx === true) endpoint = endpoint + separ + toQueryString(ctx);
		else if (Array.isArray(addCtx)) {
			let qs = toQueryString(ctx, addCtx);
			if (qs.length > 0) qs = separ + qs;
			endpoint = endpoint + qs;
		}
	}
	return endpoint;
}

/// Test if the two contexts are the same (same keys and values)
export function sameContext(ctx1:AppContextMap|null|undefined, ctx2:AppContextMap|null|undefined):boolean {
	if (ctx1 == null) ctx1 = {};
	if (ctx2 == null) ctx2 = {};

	const keys1 = Object.keys(ctx1);
	const keys2 = Object.keys(ctx2);

	if (keys1.length !== keys2.length) return false;

	for (const key of keys1) {
		if (!Object.hasOwn(ctx2, key)) return false;
		if (ctx1[key] !== ctx2[key]) return false;
	}
	return true;
}


export class AppContext {
    map: AppContextMap;

    constructor(map?:AppContextMap) {
        this.map = map || {};
    }

    clone():AppContext {
        return new AppContext({ ...this.map });
    }

	getMap() {
		return this.map;
	}

	clear() {
		this.map = {};
	}

    add(key: string, value: string | number | boolean):AppContext {
        this.map[key] = value;
        return this;
    }

    addTruthy(key: string, value: string | number | boolean):AppContext {
        if (value) this.map[key] = value;
        return this;
    }

	has(key: string):boolean {
		const v = this.map[key];
		return !(v == null || v === 0 || v === "0");
	}

	get(key: string):string|number|boolean|undefined {
		return this.map[key];
	}

	getInt(key: string):number|undefined {
		let v = this.map[key];
		switch (typeof v) {
			case "number":
				return v;
			case "boolean":
				return v ? 1 : 0;
			case "string":
				return parseInt(v);
			default:
				return undefined;
		}
	}

    remove(key:string):AppContext {
        delete this.map[key];
        return this;
    }

    mergeWith(ctx: AppContext) {
        Object.assign(this.map, ctx.map);
    }

    toString():string {
        const params = [];
        for (let key of Object.keys(this.map)) {
            const value = this.map[key];
            params.push(key+"="+encodeURIComponent(value.toString()));
        }
        return params.join("&");
    }

	isEmpty():boolean {
		const keys = Object.keys(this.map);
		if (keys.length === 0) return true;
		for (let key of keys) {
			const value = this.map[key];
			if (value) return false;
		}
		return true;
	}

    static parse(context:string):AppContext {
        const map:AppContextMap = {};
        if (context) {
            if (context.startsWith("?")) context = context.substring(1);
            const url = new URL("http://sample.com?" + context);
            for (let [key, value] of url.searchParams.entries()) {
                map[key] = value;
            }
        }
        return new AppContext(map);
    }
}
